import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import {Edit, Restore} from '@mui/icons-material';
import {MarkdownText, RichTextField} from '~/components/MarkdownEditor/MarkdownEditor';

type Props = {
    /** The team's own text (Markdown); empty until somebody writes one. */
    specification: string;
    /** The category's definition in the taxonomy (taxonomy builder description), the default specification. */
    taxonomyDescription: string;
    canEdit: boolean;
    onSave: (markdown: string) => Promise<unknown>;
};

/**
 * The category specification: the team's text, or the taxonomy definition until they write one. Edited as rich text
 * (bold, italic, lists, links) and stored as Markdown; editing starts from the taxonomy definition. "Use taxonomy
 * definition" clears the team's text again.
 */
export const CategorySpecification: React.FC<Props> = ({specification, taxonomyDescription, canEdit, onSave}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const own = specification.trim();
    const fromTaxonomy = !own && Boolean(taxonomyDescription.trim());
    const shown = own || taxonomyDescription.trim();

    const startEdit = () => {
        setDraft(shown);
        setEditing(true);
    };
    const save = () => {
        setEditing(false);
        const next = draft.trim();
        // Saving the taxonomy definition unchanged keeps following the taxonomy.
        if (next === own || (!own && next === taxonomyDescription.trim())) return;
        void onSave(next).catch(() => undefined);
    };

    return (
        <Box sx={{minWidth: 0}}>
            <Stack direction="row" alignItems="center" gap={1} sx={{minHeight: 30}}>
                <Typography variant="overline" color="text.secondary">Category specification</Typography>
                {fromTaxonomy && <Typography variant="caption" color="text.secondary">· from the taxonomy definition</Typography>}
                {canEdit && !editing && (
                    <Stack direction="row" gap={0.5} sx={{ml: 'auto'}}>
                        {own && taxonomyDescription.trim() && (
                            <Button size="small" startIcon={<Restore/>} onClick={() => void onSave('').catch(() => undefined)}>
                                Use taxonomy definition
                            </Button>
                        )}
                        <Button size="small" startIcon={<Edit/>} onClick={startEdit}>Edit</Button>
                    </Stack>
                )}
            </Stack>
            {editing ? (
                <Stack gap={1}>
                    <RichTextField initialMarkdown={draft} onChange={setDraft} ariaLabel="Category specification" autoFocus
                                   minHeight={96} placeholder="Describe what this category covers"
                                   onSubmit={save} onCancel={() => setEditing(false)}/>
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={save}>Save</Button>
                    </Stack>
                </Stack>
            ) : shown ? (
                <Box onDoubleClick={() => canEdit && startEdit()}><MarkdownText markdown={shown}/></Box>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {canEdit ? 'No specification yet, and the taxonomy has no definition for this category.' : 'No specification yet.'}
                </Typography>
            )}
        </Box>
    );
};
